<template>
  <d-card
    :class="[
      'card-small d-inline-flex',
      { show: show, 'open-below': openBelow },
    ]"
    :style="{ top: `${positionTop}px`, left: `${positionLeft}px` }"
  >
    <d-card-body class="px-2 py-0">
      <d-form-row>
        <d-col class="form-group">
          <label>{{ $t('puzzle.arrowsModal.position') }}</label>
          <d-select v-model="cell.arrow_position">
            <option value="">
              {{ $t('puzzle.arrowsModal.positionPlaceholder') }}
            </option>
            <option
              :value="option"
              v-for="option in positionAvallable"
              :key="option.id"
            >
              {{ option.name }}
            </option>
          </d-select>
        </d-col>
        <d-col class="form-group">
          <label>{{ $t('puzzle.arrowsModal.direction') }}</label>
          <d-select v-model="cell.arrow_direction">
            <option value="">
              {{ $t('puzzle.arrowsModal.directionPlaceholder') }}
            </option>
            <option
              :value="option"
              v-for="option in directionAvallable"
              :key="option.id"
            >
              {{ option.name }}
            </option>
          </d-select>
        </d-col>
      </d-form-row>
      <d-form-row>
        <d-col class="d-flex justify-content-between">
          <d-button
            type="button"
            theme="light"
            class="w-100 mr-2"
            @click="close()"
            >{{ $t('shared.actions.cancel') }}</d-button
          >
          <d-button type="button" class="w-100 ml-2" @click="save()"
            >{{ $t('shared.actions.save') }}</d-button
          >
        </d-col>
      </d-form-row>
    </d-card-body>
  </d-card>
</template>

<script>
import { mapActions } from 'vuex';
import modalPositionMixin from '_common/mixins/modalPositionMixin';
import { cloneDeep } from '_utils';
import { ARROW, CELL, CELL_SIZE, _PUZZLE_ARROW_OPTION_ } from '../PuzzleModel';
import puzzleReadOnly from '../../mixins/puzzleReadOnly';

const ARROWS_CELL_OFFSET_TOP = 102;
const ARROWS_CELL_OFFSET_LEFT = 110;

export default {
  name: 'puzzle-option-arrow-modal',
  mixins: [modalPositionMixin, puzzleReadOnly],
  computed: {
    puzzle() {
      const { puzzle } = this.$store.state.puzzle;
      if (puzzle) {
        return puzzle;
      }
      return false;
    },
    arrowPositions() {
      const { arrowPositions } = this.$store.state.puzzle;
      if (arrowPositions) {
        return arrowPositions;
      }
      return [cloneDeep(ARROW)];
    },
    arrowDirections() {
      const { arrowDirections } = this.$store.state.puzzle;
      if (arrowDirections) {
        return arrowDirections;
      }
      return [cloneDeep(ARROW)];
    },
    positionAvallable() {
      return this.arrowPositions.filter(arrow => {
        if (arrow.slug) {
          const direction = arrow.slug.split('_');
          const initial = direction[0];
          const last = direction.pop();
          if (this.puzzle) {
            return (
              this[initial](arrow) &&
              this[last](arrow) &&
              this.mergedCell(direction, arrow)
            );
          }
        }
        return false;
      });
    },
    directionAvallable() {
      const { slug: position } = this.cell.arrow_position
        ? this.cell.arrow_position
        : cloneDeep(ARROW);
      const directions = position ? position.split('_') : [];
      const invertedPosition = {
        top: 'bottom',
        left: 'right',
        right: 'left',
        bottom: 'top',
      };

      return this.arrowDirections.filter(arrow => {
        if (position === null) return arrow;
        if (invertedPosition[directions[0]] !== arrow.slug) {
          if (
            position.indexOf('diagonal') > -1 &&
            invertedPosition[directions[2]] === arrow.slug
          ) {
            return false;
          }
          return arrow;
        }
        return false;
      });
    },
  },
  data() {
    return {
      cell: cloneDeep(CELL),
    };
  },
  methods: {
    ...mapActions(['getArrowDirections', 'getArrowPositions']),
    mergedCell(direction, arrow) {
      if (direction[1] && direction[1].includes('second')) {
        if (
          Number.isNaN(Number(this.cell.positiony)) ||
          this.cell.type.includes('rowspan') ||
          Number.isNaN(Number(this.cell.positionx)) ||
          this.cell.type.includes('colspan')
        ) {
          return arrow;
        }
        return false;
      }
      return true;
    },
    top(item) {
      let { positiony } = this.cell;
      if (Number.isNaN(Number(positiony))) {
        positiony = Number(positiony.split('_').shift());
      }
      if (positiony !== 0) {
        return item;
      }
      return false;
    },
    bottom(item) {
      let { positiony } = this.cell;
      if (Number.isNaN(Number(positiony))) {
        positiony = Number(positiony.split('_').shift());
      }
      if (positiony < this.puzzle.rows) {
        return item;
      }
      return false;
    },
    right(item) {
      if (this.cell.positionx !== this.puzzle.columns - 1) {
        return item;
      }
      return false;
    },
    left(item) {
      if (this.cell.positionx !== 0) {
        return item;
      }
      return false;
    },
    save() {
      if (!this.guardPuzzleMutation()) return;

      this.cell.arrow_position_id = this.cell.arrow_position.id;
      this.cell.arrow_direction_id = this.cell.arrow_direction.id;
      this.cell.updated = true;
      this.close();
    },
    getOptions() {
      if (!this.$store.state.puzzle.arrowDirections) {
        this.getArrowDirections();
      }
      if (!this.$store.state.puzzle.arrowPositions) {
        this.getArrowPositions();
      }
    },
  },
  mounted() {
    this.getOptions();
    this.registerModalEvents(_PUZZLE_ARROW_OPTION_, (cell, cellRect) => {
      if (this.isReadOnly) return;

      this.cell = cell;
      this.show = true;
      if (cellRect) {
        this.applyPosition(cellRect);
      } else {
        this.openBelow = false;
        this.positionTop = cell.positiony * CELL_SIZE - ARROWS_CELL_OFFSET_TOP;
        this.positionLeft =
          cell.positionx * CELL_SIZE + CELL_SIZE / 2 - ARROWS_CELL_OFFSET_LEFT;
      }
    });
  },
};
</script>

<style lang="scss" scoped>
.card-small {
  visibility: hidden;
  transform: translateY(-100%);
  border-radius: 6px;
  padding: 5px 0;
  position: absolute;
  z-index: 1199;
  transition: all 0.5 ease-in;
  background: #d7d7d7;
  width: 230px;
  &:before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -8px;
    border-width: 8px;
    border-style: solid;
    border-color: #d7d7d7 transparent transparent transparent;
  }
  &.open-below {
    transform: none;
  }
  &.open-below:before {
    top: auto;
    bottom: 100%;
    border-color: transparent transparent #d7d7d7 transparent;
  }
  &.show {
    visibility: visible;
    transition: all 0.5 ease-in;
  }
}
</style>
